// src/services/api/api.ts

import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { useAuthStore } from '@/domains/auth/auth.store';
import { handleAppError } from '@/shared/notifications/ErrorManager';
import { createAppError } from '@/shared/errors';
import { VITE_API_BASE_URL } from '@/config';
import { API_ENDPOINTS } from './endpoints';
import { showErrorToast } from '@/shared/notifications/toast';
import i18n from '@/shared/i18n/i18n';
import { ApiRequestOptions, ApiRequestBlobResponse } from './api.types';
import { NAVBAR_SOURCES } from '@/components/AppComponents/Sidebar/NavBar/NavBarSources';
import { getRefreshTokenOnce } from './refreshTokenManager';

export const apiClient = axios.create({
  baseURL: VITE_API_BASE_URL,
  paramsSerializer: {
    indexes: null, // this will serialize array params without indexes: e.g. ?param=value1&param=value2 instead of ?param[0]=value1&param[1]=value2
  },
  headers: {
    'Content-Type': 'application/json',
  },
});

// Axios client without refresh interceptors
// This will be used specifically for login, registration, and token refresh,
// so we can handle their errors directly.
export const apiAuthClient = axios.create({
  baseURL: VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export async function apiRequest<T, P>(
  method: AxiosRequestConfig['method'],
  url: string,
  data?: P, // default value
  auth = false,
  options?: ApiRequestOptions<P>,
): Promise<AxiosResponse<T>> {
  // Interpolate parameters in URL if pathParams are defined
  let finalUrl = url;
  if (options?.pathParams) {
    for (const [key, value] of Object.entries(options.pathParams)) {
      finalUrl = finalUrl.replace(`:${key}`, value);
    }
  }
  // add headers if auth is true
  const headers = auth
    ? { Authorization: `Bearer ${useAuthStore.getState().accessToken}` }
    : undefined;

  const config: AxiosRequestConfig = {
    method,
    url: finalUrl,
    headers,
    responseType: options?.responseType,
  };

  // For query params:
  // If they are defined in options, those are used; otherwise, the data parameter can be used in GET.
  if (options?.queryParams) {
    config.params = options.queryParams;
  } else if (method === 'get' && data) {
    config.params = data;
  }

  // For the body:
  // Options.body is prioritized in non-GET methods. If not defined, data is used.
  if (method !== 'get') {
    config.data = options?.body ?? data;
  }

  return apiClient(config);
}

export async function apiRequestBlob(
  url: string,
  options?: Omit<ApiRequestOptions<undefined>, 'responseType'>,
  fallbackFilename = 'download',
): Promise<ApiRequestBlobResponse> {
  const response = await apiRequest<Blob, undefined>(
    'get',
    url,
    undefined,
    true,
    { ...options, responseType: 'blob' },
  );

  // Extract filename from content-disposition header
  const contentDisposition = response.headers['content-disposition'];
  let filename = fallbackFilename;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
    if (filenameMatch?.[1]) {
      filename = filenameMatch[1].replace(/["']/g, '').trim();
    }
  }

  return { blob: response.data, filename };
}

// Interceptor to manage errors 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    const { refreshToken: storedRefreshToken, clearTokens } =
      useAuthStore.getState();

    const isAuthError = error.response?.status === 401;
    // It is crucial that the interceptor does NOT attempt to refresh if the refresh token call itself fails
    const isNotLoginOrOtpOrRefreshEndpoint =
      !originalRequest?.url?.includes(API_ENDPOINTS.AUTH_LOGIN) &&
      !originalRequest?.url?.includes(API_ENDPOINTS.AUTH_TOKEN) &&
      !originalRequest?.url?.includes(API_ENDPOINTS.AUTH_REFRESH_TOKEN);

    if (isAuthError && storedRefreshToken && isNotLoginOrOtpOrRefreshEndpoint) {
      let newAccessToken: string;
      try {
        newAccessToken = await getRefreshTokenOnce(); // This function will make the call with 'apiAuthClient'
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError);
        clearTokens();
        showErrorToast(i18n.t('login.errors.refreshTokenFailed'));

        if (window.location.pathname !== NAVBAR_SOURCES.login) {
          // store a flag to display a message in the login page
          localStorage.setItem('sessionExpiredMessage', 'true');
          // ... and redirect to login
          window.location.assign(NAVBAR_SOURCES.login);
        }

        return Promise.reject(createAppError(refreshError));
      }

      if (!originalRequest) {
        return Promise.reject(
          createAppError(new Error('No original request to retry')),
        );
      }
      if (!originalRequest.headers) {
        originalRequest.headers = new AxiosHeaders();
      }
      (originalRequest.headers as AxiosHeaders).set(
        'Authorization',
        `Bearer ${newAccessToken}`,
      );
      return apiClient.request(originalRequest); // Retry — interceptor handles any errors from the retry
    }

    const appError = createAppError(
      error ?? new Error('Unhandled error occurred'),
    );
    // Skip toast for validation so forms can show a single i18n or backend message
    if (appError.kind !== 'validation') {
      handleAppError(appError);
    }
    return Promise.reject(appError);
  },
);
